// ───────── Formatters ─────────

export const formatCurrency = (v, moneda = "COP") => {
  // Los Decimal vienen como string del back — convertir con cuidado.
  const num = typeof v === "string" ? parseFloat(v) : Number(v)
  if (!isFinite(num)) return "—"
  try {
    return new Intl.NumberFormat("es-CO", {
      style: "currency", currency: moneda || "COP",
      maximumFractionDigits: 0,
    }).format(num)
  } catch {
    // Si moneda no es ISO válida, fallback sin símbolo
    return new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(num)
  }
}

export const formatNumber = (v, decimals = 0) => {
  const num = typeof v === "string" ? parseFloat(v) : Number(v)
  if (!isFinite(num)) return "—"
  return new Intl.NumberFormat("es-CO", {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(num)
}

export const formatPercent = (v, decimals = 1) => {
  const num = typeof v === "string" ? parseFloat(v) : Number(v)
  if (!isFinite(num)) return "—"
  return `${num.toFixed(decimals)}%`
}

export const formatFecha = iso => {
  if (!iso) return "—"
  try {
    const s = String(iso).substring(0, 10)
    const [y, m, d] = s.split("-")
    if (y && m && d) return `${d}/${m}/${y}`
    return iso
  } catch { return iso }
}

export const formatFechaHora = iso => {
  if (!iso) return "—"
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleString("es-CO", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    })
  } catch { return iso }
}

// Para inputs <input type="date"> — convierte ISO o Date a "YYYY-MM-DD"
export const toDateInput = (v) => {
  if (!v) return ""
  try {
    const s = String(v)
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10)
    const d = new Date(v)
    if (isNaN(d.getTime())) return ""
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
  } catch { return "" }
}

// ───────── Paleta de estados ─────────

export const ESTADO_CONFIG = {
  BORRADOR:  { color: "#64748b", bg: "#f1f5f9", label: "Borrador",  icon: "mdi-pencil-outline",       editable: true  },
  APROBADO:  { color: "#0284c7", bg: "#e0f2fe", label: "Aprobado",  icon: "mdi-check-decagram-outline", editable: false },
  ACTIVO:    { color: "#16a34a", bg: "#dcfce7", label: "Activo",    icon: "mdi-rocket-launch-outline",  editable: false },
  CERRADO:   { color: "#475569", bg: "#e2e8f0", label: "Cerrado",   icon: "mdi-lock-outline",          editable: false },
  CANCELADO: { color: "#dc2626", bg: "#fee2e2", label: "Cancelado", icon: "mdi-cancel",                editable: false },
}

export const estadoConfig = e => {
  const k = String(e || "").toUpperCase()
  return ESTADO_CONFIG[k] || { color: "#64748b", bg: "#f1f5f9", label: e || "—", icon: "mdi-help-circle-outline", editable: false }
}

// Transiciones válidas (espejo de la regla del back)
export const TRANSICIONES_VALIDAS = {
  BORRADOR:  ["APROBADO", "CANCELADO"],
  APROBADO:  ["ACTIVO", "CANCELADO"],
  ACTIVO:    ["CERRADO", "CANCELADO"],
  CERRADO:   [],
  CANCELADO: [],
}

export const transicionesPosibles = (estadoActual) =>
  TRANSICIONES_VALIDAS[String(estadoActual || "").toUpperCase()] || []

// ───────── Periodicidad ─────────

export const PERIODICIDADES = [
  { value: "DIARIO",        label: "Diario",        descripcion: "Un periodo por día" },
  { value: "SEMANAL",       label: "Semanal",       descripcion: "Cada 7 días" },
  { value: "QUINCENAL",     label: "Quincenal",     descripcion: "Quincenas calendario" },
  { value: "MENSUAL",       label: "Mensual",       descripcion: "Un periodo por mes" },
  { value: "BIMESTRAL",     label: "Bimestral",     descripcion: "Bloques de 2 meses" },
  { value: "TRIMESTRAL",    label: "Trimestral",    descripcion: "Bloques de 3 meses" },
  { value: "SEMESTRAL",     label: "Semestral",     descripcion: "Bloques de 6 meses" },
  { value: "ANUAL",         label: "Anual",         descripcion: "Un periodo por año" },
  { value: "PERSONALIZADO", label: "Personalizado", descripcion: "Un único periodo con rango completo" },
]

export const periodicidadLabel = (p) => {
  const found = PERIODICIDADES.find(x => x.value === String(p || "").toUpperCase())
  return found ? found.label : p
}

// ───────── Tipo de medición ─────────

export const TIPOS_MEDICION = [
  { value: "MONTO",    label: "Solo monto",  desc: "Objetivo en valor monetario" },
  { value: "UNIDADES", label: "Solo unidades", desc: "Objetivo en cantidad de unidades vendidas" },
  { value: "AMBOS",    label: "Monto + Unidades", desc: "Ambos objetivos en paralelo" },
]

export const muestraMonto = (tipo) =>
  String(tipo || "").toUpperCase() !== "UNIDADES"

export const muestraUnidades = (tipo) =>
  ["UNIDADES", "AMBOS"].includes(String(tipo || "").toUpperCase())

// ───────── Permisos ─────────

/**
 * Lee `permisos` del usuario en localStorage. Tolerante a varias shapes
 * (array de strings, objeto con boolean, etc.) hasta saber cuál es la canónica.
 */
export const hasPermission = (clave) => {
  try {
    const auth = JSON.parse(localStorage.getItem("authUser") || "{}")
    const usuario = auth.usuario || auth
    const permisos = usuario.permisos_globales || usuario.permisos || []
    if (Array.isArray(permisos)) return permisos.includes(clave)
    if (typeof permisos === "object") return Boolean(permisos[clave])
  } catch { /* ignore */ }
  // Fallback: si no hay info de permisos, permitimos (back validará)
  return true
}

// ───────── Otros ─────────

export const TIPOS_CATEGORIA = ["LINEA", "CANAL", "MARCA", "PRODUCTO", "OTRO"]

export const monedaList = ["COP", "USD", "EUR", "MXN", "PEN", "ARS", "CLP"]

// Lista de años por defecto para selectors
const ahora = new Date()
export const ANIOS_DISPONIBLES = Array.from({ length: 6 }, (_, i) => ahora.getFullYear() - 1 + i)

// ───────── Semáforo de cumplimiento ─────────

export const cumplimientoConfig = (pct) => {
  const p = Number(pct)
  if (!isFinite(p)) return { color: "#64748b", bg: "#f1f5f9", label: "—", icon: "mdi-help-circle-outline", status: "n/a" }
  if (p >= 100) return { color: "#16a34a", bg: "#dcfce7", label: "Superado",    icon: "mdi-trophy-outline",          status: "superado" }
  if (p >= 95)  return { color: "#16a34a", bg: "#dcfce7", label: "Cumplido",    icon: "mdi-check-circle-outline",    status: "cumplido" }
  if (p >= 80)  return { color: "#ca8a04", bg: "#fef9c3", label: "En riesgo",   icon: "mdi-alert-circle-outline",    status: "riesgo" }
  if (p > 0)    return { color: "#dc2626", bg: "#fee2e2", label: "Bajo",        icon: "mdi-alert-octagon-outline",   status: "bajo" }
  return { color: "#7f1d1d", bg: "#fee2e2", label: "Sin venta", icon: "mdi-close-circle-outline", status: "sin_venta" }
}

// Cap visual del % cumplimiento (para barras): cortamos en 150% para mantener
// escala legible; mostramos el valor real en el texto.
export const capPct = (pct, max = 150) => {
  const p = Number(pct) || 0
  return Math.min(Math.max(p, 0), max)
}
